import * as Msdyn365 from '@msdyn365-commerce/core';

export interface IOrderDetailsExtensionProperty {
    Key: string;
    Value: {
        StringValue?: string;
        DecimalValue?: number;
        IntegerValue?: number;
        DateTimeOffsetValue?: string;
        BooleanValue?: boolean;
        [key: string]: unknown;
    };
}

export interface IOrderDetailsTaxLine {
    Amount: number;
    ExtensionProperties?: IOrderDetailsExtensionProperty[];
}

export interface IOrderDetailsSalesLine {
    ItemId: string;
    ItemTaxGroupId?: string;
    NetAmount: number;
    NetAmountWithAllInclusiveTax?: number;
    Price: number;
    Quantity: number;
    SalesTaxGroupId?: string;
    Description?: string;
    UnitOfMeasureSymbol?: string;
    DiscountAmount?: number;
    LineDiscount?: number;
    LineNumber?: number;
    ItemRecId: number;
    LinePercentageDiscount?: number;
    GrossAmount?: number;
    RetailColor?: string;
    TenderDiscountAmount?: number;
    TenderDiscountPercentage?: number;
    TotalDiscount?: number;
    TotalPercentageDiscount?: number;
    TaxLines?: IOrderDetailsTaxLine[];
    ExtensionProperties?: IOrderDetailsExtensionProperty[];
}

export interface IOrderDetailsShippingAddress {
    Name?: string;
    Street?: string;
    ZipCode?: string;
    City?: string;
    FullAddress?: string;
    ThreeLetterISORegionName?: string;
    ExtensionProperties?: IOrderDetailsExtensionProperty[];
}

export interface IOrderDetailsSalesOrder {
    '@odata.context'?: string;
    SalesId: string;
    CreatedDateTime: string;
    RequestedDeliveryDate?: string;
    TotalAmount: number;
    CurrencyCode?: string;
    CustomerRequisition?: string;
    Comment?: string;
    DeliveryMode?: string;
    SubtotalAmount?: number;
    TaxAmount?: number;
    TotalDiscount?: number;
    ShippingCosts?: number;
    TotalCharge?: number;
    SalesLines?: IOrderDetailsSalesLine[];
    ShippingAddress?: IOrderDetailsShippingAddress;
    ExtensionProperties?: IOrderDetailsExtensionProperty[];
    Notes?: IOrderDetailsNote[];
    SalesStatus?: string | number;
    PackingSlips?: IOrderDetailsPackingSlip[];
    Invoices?: IOrderDetailsInvoice[];
}

export interface IOrderDetailsPackingSlip {
    PackingSlipId: string;
    ExtensionProperties?: IOrderDetailsExtensionProperty[];
}

export interface IOrderDetailsInvoice {
    InvoiceId: string;
    ExtensionProperties?: IOrderDetailsExtensionProperty[];
}

export interface IOrderDetailsNote {
    Text: string;
    ExtensionProperties?: IOrderDetailsExtensionProperty[];
}

export interface IGetSalesOrderDetailsResponse {
    result?: IOrderDetailsSalesOrder;
    error?: {
        errorCode: number;
        errorText: string;
    };
}

export class GetSalesOrderDetailsInput implements Msdyn365.IActionInput {
    public userToken: string;
    public timestamp: string;

    public salesId: string;
    public transactionId: string;

    constructor(ctx: Msdyn365.ICreateActionContext, salesId: string, transactionId: string) {
        this.userToken = ctx.requestContext.user.token;
        this.timestamp = new Date().getTime().toString();

        this.salesId = salesId;
        this.transactionId = transactionId;
    }

    public getCacheKey = () => `GetSalesOrderDetails-${this.userToken}-${this.salesId}-${this.transactionId}-${this.timestamp}`;

    public getCacheObjectType = () => 'GetSalesOrderDetails';

    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const salesId: string = (args.requestContext.query && (args.requestContext.query as any).salesId) || '';

    const transactionId: string = (args.requestContext.query && (args.requestContext.query as any).transactionId) || '';

    return new GetSalesOrderDetailsInput(args, salesId, transactionId);
};

async function action(input: GetSalesOrderDetailsInput, ctx: Msdyn365.IActionContext): Promise<IGetSalesOrderDetailsResponse> {
    const userToken = ctx.requestContext.user.token === null ? 'USERTOKEN' : ctx.requestContext.user.token;

    const response = await Msdyn365.sendRequest<IOrderDetailsSalesOrder>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetSalesOrderDetails(salesId='${input.salesId}',transactionId='${input.transactionId}')`,

        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${userToken}`
            }
        }
    );
    if (response.status === 200 && response.data) {
        return { result: response.data };
    }

    return {
        error: {
            errorCode: response.status,
            errorText: response.statusText
        }
    };
}

export default Msdyn365.createObservableDataAction({
    id: 'GetSalesOrderDetails',
    action: action as Msdyn365.IAction<IGetSalesOrderDetailsResponse>,
    input: createInput
});
